<template>
  <div class="ma-4 pt-4">
    <v-row>
      <v-col class="col-12">
        <v-card outlined>
          <v-dialog
            v-model="dialog"
            fullscreen
            hide-overlay
            transition="dialog-bottom-transition"
          >
            <v-card tile>
              <v-toolbar dark color="primary">
                <v-toolbar-title
                  >{{ $t('priceHistory.viewTitle') }},
                  {{ selectedDate | formatDate }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon @click="dialog = false">
                  <i class="pl-2 fas fa-times mr-2 text--white" />
                </v-btn>
              </v-toolbar>
              <v-card-text class="pa-4">
                <price-history
                  :selected-product-definitions="selectedDefinitions"
                  :price-history-data="currentPriceHistory"
                  :calculation-date="selectedDate"
                >
                </price-history>
              </v-card-text>
            </v-card>
          </v-dialog>

          <v-tabs
            v-model="tab"
            background-color="white accent-4"
            icons-and-text
            class="rounded-top"
          >
            <v-tabs-slider></v-tabs-slider>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-tab
                  :style="{ width: '150px' }"
                  href="#price-tab"
                  v-bind="attrs"
                  v-on="on"
                >
                  <i class="tab-icon fal fa-analytics"></i>
                </v-tab>
              </template>
              <span> {{ this.$t('overviewPricing.postedPrices') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-tab
                  v-if="!isStaticPriced"
                  :style="{ width: '150px' }"
                  href="#targetprice-tab"
                  :disabled="isStaticPriced"
                  v-bind="attrs"
                  v-on="on"
                >
                  <i class="tab-icon fal fa-bullseye-pointer"></i>
                </v-tab>
              </template>
              <span>{{
                this.$t('overviewPricing.targetPrices') +
                ` (${$store.getters
                  .getCurrentDestinationInstance()
                  .getCurrency()})`
              }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-tab
                  :style="{ width: '150px' }"
                  href="#override-tab"
                  v-bind="attrs"
                  v-on="on"
                >
                  <i class="tab-icon far fa-money-check-edit-alt"></i
                ></v-tab>
              </template>
              <span>{{ this.$t('overviewPricing.overwrite') }}</span></v-tooltip
            >
          </v-tabs>
          <v-card-text>
            <v-tabs-items
              v-if="products && dateRange && selectedDate"
              v-model="tab"
            >
              <!-- v-if condition is required for proper chartjs rendering. See: https://pricenow.atlassian.net/browse/T1-1814 -->
              <v-tab-item
                v-if="tab === 'price-tab'"
                value="price-tab"
              >
                <current-posted-prices
                  chartId="currentPostedPrices"
                  disableXAxis
                  :date-range="currentRange"
                  :current-posted-prices="currentPostedPrices"
                  :selected-date="selectedDate"
                  :selected-definitions="selectedDefinitions"
                  @currentDate="dialog = true"
                ></current-posted-prices>

                <realized-demand-chart
                  v-if="!isStaticPriced"
                  key="realizedDemand"
                  :date-range="currentRange"
                  :selected-date="selectedDate"
                  :selected-definitions="selectedDefinitions"
                  :realized-demand="realizedDemand"
                >
                </realized-demand-chart>
                <date-range-slider
                  :dateRange="dateRange"
                  :currentRange="currentRange"
                  @currentDateChange="updateDateRange"
                />
              </v-tab-item>
              <!-- v-if condition is required for proper chartjs rendering. See: https://pricenow.atlassian.net/browse/T1-1814 -->
              <v-tab-item
                v-if="tab === 'targetprice-tab'"
                value="targetprice-tab"
              >
                <product-target-prices-chart
                  v-if="!isStaticPriced"
                  :date-range="currentRange"
                  :selected-date="selectedDate"
                  :set-selected-date-store-path="'OverviewPricing/setSelectedDate'"
                  :product-target-prices="productTargetPrices"
                  :selected-definitions="selectedDefinitions"
                ></product-target-prices-chart>
                <date-range-slider
                  :dateRange="dateRange"
                  :currentRange="currentRange"
                  @currentDateChange="updateDateRange"
                />
              </v-tab-item>
              <!-- v-if condition is required for proper chartjs rendering. See: https://pricenow.atlassian.net/browse/T1-1814 -->
              <v-tab-item
                v-if="tab === 'override-tab'"
                value="override-tab"
              >
                <current-posted-prices
                  chartId="currentPostedPrices2"
                  disableXAxis
                  :date-range="currentRange"
                  :current-posted-prices="currentPostedPrices"
                  :selected-date="selectedDate"
                  :selected-definitions="selectedDefinitions"
                  @currentDate="dialog = true"
                ></current-posted-prices>
                <date-range-slider
                  :dateRange="dateRange"
                  :currentRange="currentRange"
                  @currentDateChange="updateDateRange"
                />
                <price-override-form
                  key="priceOverrideForm"
                  :date-range="currentRange"
                  :chosen-products="products"
                  :selected-product-definitions="selectedDefinitions"
                  :current-selected-prices="currentSelectedPrices"
                  hideProductSelection
                  @postOverride="loadViewData()"
                />
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col class="col-12">
        <!-- product selection table -->
        <v-card outlined>
          <v-card-text>
            <v-row no-gutters>
              <v-col class="col-12">
                <product-definition-table
                  v-if="products && dateRange && selectedDate"
                  :products="products"
                  :selected-date="selectedDate"
                  :preselection="selectedDefinitions"
                  :store-remove-definition-path="'OverviewPricing/removeSelectedDefinition'"
                  :store-add-definition-path="'OverviewPricing/addSelectedDefinition'"
                  :store-set-selected-definitions-path="'OverviewPricing/setSelectedDefinitions'"
                  :store-get-selected-definitions-path="'OverviewPricing/selectedDefinitions'"
                  alignment="horizontal"
                />
                <!-- skeleton loader -->
                <v-skeleton-loader v-else type="table" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import DateRangeSlider from '@/components-internal/dateRange/DateRangeSlider'
import DateRange from '@/classes-shared/dates/DateRange'
import ProductDefinitionTable from '@/components-internal/productDefinitionTable/ProductDefinitionTable'
import CurrentPostedPrices from '@/components-internal/charts/CurrentPostedPricesChart'
import ProductTargetPricesChart from '@/components-internal/charts/ProductTargetPricesChart'
import { mapState } from 'vuex'
import PriceOverrideForm from '@/components-internal/priceOverride/PriceOverrideForm'
import RealizedDemandChart from '@/components-internal/charts/RealizedDemandChart'
import PriceHistory from './PriceHistory.vue'

export default {
  name: 'OverviewPricing',
  components: {
    PriceOverrideForm,
    CurrentPostedPrices,
    ProductDefinitionTable,
    DateRangeSlider,
    ProductTargetPricesChart,
    RealizedDemandChart,
    PriceHistory,
  },
  data() {
    return {
      tab: 'price-tab',
      dialog: false,
      dateRange: new DateRange(),
      currentRange: new DateRange(),
    }
  },
  computed: {
    ...mapState('OverviewPricing', [
      'selectedDate',
      'selectedDefinitions',
      'currentPriceHistory',
      'productTargetPrices',
      'currentPostedPrices',
      'currentSelectedPrices',
      'realizedDemand',
    ]),
    // selected products
    products() {
      return this.$store.getters.getProducts()
    },
    selectedSeason() {
      return this.$store.getters.getSelectedSeason()
    },
    isStaticPriced() {
      return this.$store.getters.isOnlyStaticallyPriced()
    },
    postedProductDefinitionIds() {
      return this.selectedDefinitions
        .filter((prodDef) => prodDef.color !== undefined)
        .map((prodDef) => prodDef.item.id)
    },
    selectedProductDefinitionIds() {
      return this.selectedDefinitions.map((prodDef) => prodDef.item.id)
    },
  },
  watch: {
    selectedDefinitions: {
      handler: function (newDefinitions) {
        this.loadViewData(newDefinitions)
      },
    },

    selectedSeason(season) {
      if (season) {
        this.initializeDateRanges(season, this.tab)
      }
    },
    products(products) {
      if (products) {
        this.initializeDateRanges(this.selectedSeason, this.tab)
      }
    },
    tab: function (newTab) {
      this.initializeDateRanges(this.selectedSeason, newTab)
    },
  },
  mounted() {
    this.initializeDateRanges(this.selectedSeason, this.tab)
  },
  methods: {
    initializeDateRanges(season, tab) {
      if (!season) return
      const today = new Date()
      const minSeasonStart = season.getSeasonStart()
      const seasonStart = today > minSeasonStart ? today : minSeasonStart
      this.dateRange.update(
        tab === 'targetprice-tab' ? seasonStart : minSeasonStart,
        season.getSeasonEnd()
      )
      this.currentRange.update(
        tab === 'targetprice-tab' ? seasonStart : minSeasonStart,
        season.getSeasonEnd()
      )
    },

    updateDateRange(range) {
      this.currentRange.update(range[0], range[1])
    },

    loadViewData(selectedDefinitions = []) {
      if (selectedDefinitions.length < 1) {
        selectedDefinitions = this.selectedDefinitions
      }
      const seasonDateRange = new DateRange(
        this.selectedSeason.getSeasonStart(),
        this.selectedSeason.getSeasonEnd()
      )
      if (this.postedProductDefinitionIds.length > 0) {
        this.$store.dispatch('OverviewPricing/loadCurrentPostedPrices', {
          from: seasonDateRange.startDate,
          to: seasonDateRange.endDate,
          postedProductDefinitionIds: this.postedProductDefinitionIds,
          selectedProductDefinitionIds: this.selectedProductDefinitionIds,
        })
        if (!this.isStaticPriced) {
          this.$store.dispatch('OverviewPricing/realizedDemands', {
            from: seasonDateRange.startDate,
            to: seasonDateRange.endDate,
            productDefIds: this.postedProductDefinitionIds,
          })
          this.$store.dispatch('OverviewPricing/fetchProductTargetPrices', {
            from: seasonDateRange.startDate,
            to: seasonDateRange.endDate,
            productDefIds: this.postedProductDefinitionIds,
          })
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.fa-1-5 {
  font-size: 1rem;
}

.rounded-top {
  border-top-left-radius: 8px;
  overflow: hidden;
  border-top-right-radius: 8px;
}

.tab-icon {
  font-size: 20px !important;
}
</style>
